import { useEffect, useState, useMemo } from 'react'
import { Oval as LoadingSpinner } from 'react-loader-spinner'
import { Logger } from '@/services/Logger'
import './CustomFieldPage.css'
import { PAGES } from '@/constants/pages'
import PermissionGuard from '@/components/PermissionGuard'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { usePartner } from '@/contexts/partnerContext'
import { PartnerCustomFieldService } from '@/services/PartnerCustomFieldService'
import { CustomField } from '@/models/customField'
import { PageLayout } from './PageLayout'
import { CUSTOM_FIELD_CONFIG } from '@/constants/customFieldConfig'
import { useLoading } from '@/contexts/LoadingContext'
import toast from '@/utils/toast'

export default function CustomFieldPage() {
  const { showLoader, hideLoader } = useLoading()
  
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set())
  const { impersonatedPartnerId } = usePartner();

  // Modal states
  const [createFieldModal, setCreateFieldModal] = useState({
    show: false,
    loading: false,
    parentId: undefined as string | undefined,
    form: { name: '', description: '' },
    errors: {} as { [key: string]: string }
  })

  const [updateFieldModal, setUpdateFieldModal] = useState({
    editingParentField: false,
    show: false,
    loading: false,
    fieldId: '',
    form: { name: '', description: '' },
    errors: {} as { [key: string]: string }
  })

  // Computed values
  const organizedFields = useMemo(() => {
    return customFields
      .map(parent => ({
        ...parent,
        subFields: parent.subFields || []
      }))
      .sort((a, b) => {
        const slotA = a.slot ?? 1;
        const slotB = b.slot ?? 1;
        return slotA - slotB;
      });
  }, [customFields]);

  // Form validation
  const validateFieldForm = (name: string, isUpdate: boolean = false): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}

    if (!name) {
      errors.name = 'Field name is required';
    } else if (name.length > 200) {
      errors.name = 'Field name must be 200 characters or less';
    } 

    if (!errors.name) {
      const allFields = customFields.flatMap(field => [field, ...(field.subFields || [])]);
      const isDuplicate = allFields.some(field => {
        const nameMatches = field.name.toLowerCase() === name.toLowerCase();
      });
  
      if (isDuplicate) {
        errors.name = 'Field name already exists';
      }
    }

    return errors
  }

  // Form handlers
  const handleCreateFormChange = (field: 'name' | 'description' | 'channelPartnerId', value: string) => {
    const updatedForm = { ...createFieldModal.form, [field]: value }
    const fields = customFields.flatMap(field => [
      field,
      ...field.subFields || []
    ]);
    const errors = validateFieldForm(updatedForm.name)

    setCreateFieldModal(prev => ({
      ...prev,
      form: updatedForm,
      errors
    }))
  }

  const handleUpdateFormChange = (field: 'name' | 'description', value: string) => {
    const updatedForm = { ...updateFieldModal.form, [field]: value }
    const errors = field === 'name' ? validateFieldForm(value, true) : {}

    setUpdateFieldModal(prev => ({
      ...prev,
      form: updatedForm,
      errors: { ...prev.errors, [field]: errors[field] || '' }
    }))
  }

  const shouldGetCustomFields = impersonatedPartnerId !== null || authService.channelPartnerId !== null;

  const fetchCustomFields = async () => {
      let active = true;
  
      if (!shouldGetCustomFields) {
        active = false;
        setCustomFields([]);
        return;
      };
  
      try {
        showLoader()
        const data = await (await PartnerCustomFieldService.getAll()).customFields
        if (active) {
          if (data == null || data.length == 0) {
            setCustomFields([])
          } else {
            setCustomFields(data)
            const defaultField = data.find(field => field.isDefault)
            if (defaultField) {
              setExpandedFields(new Set([defaultField.id]))
            }
          }
        }
      } catch (err) {
        toast.error('Failed to fetch custom fields.');
      } finally {
        if (active) {
          hideLoader()
          active = false;
        }
      }
    }

  useEffect(() => {
    fetchCustomFields();
  }, [impersonatedPartnerId]); 

  // UI handlers
  const toggleFieldExpansion = (fieldId: string) => {
    setExpandedFields(prev => {
      const newSet = new Set(prev)
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId)
      } else {
        newSet.add(fieldId)
      }
      return newSet
    })
  }

  // Modal handlers
  const openCreateModal = (parentId?: string) => {
    setCreateFieldModal({
      show: true,
      loading: false,
      parentId,
      form: { name: '', description: '' },
      errors: {}
    })
  }

  const openUpdateModal = (id: string, name: string, isParentField: boolean, description?: string) => {
    setUpdateFieldModal({
      editingParentField: isParentField,
      show: true,
      loading: false,
      fieldId: id,
      form: { name: name, description: description || '' },
      errors: {}
    })
  }

  // Form submission handlers
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    const formName = createFieldModal.form.name.trim()
    const formDescription = createFieldModal.form.description.trim()

    const errors = validateFieldForm(formName)
    setCreateFieldModal(prev => ({ ...prev, errors }))

    if (Object.keys(errors).length > 0) {
      return
    }

    setCreateFieldModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerCustomFieldService.create(formName, createFieldModal.parentId, formDescription);
      toast.success('Custom field has successfully been created.');
      const response = await PartnerCustomFieldService.getAll()
      setCustomFields(response.customFields)

      setCreateFieldModal({
        show: false,
        loading: false,
        parentId: undefined,
        form: { name: '', description: '' },
        errors: {}
      })
    } catch (error) {
      toast.error('Failed to create custom field.');
      setCreateFieldModal(prev => ({ ...prev, loading: false }))
    }
  }

  const submitUpdate = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors = validateFieldForm(updateFieldModal.form.name, true)
    setUpdateFieldModal(prev => ({ ...prev, errors }))

    if (Object.keys(errors).length > 0) {
      return
    }

    setUpdateFieldModal(prev => ({ ...prev, loading: true }))

    try {
      await PartnerCustomFieldService.update(updateFieldModal.fieldId, updateFieldModal.form.name, updateFieldModal.form.description)
      toast.success('Custom field has successfully been updated.');
      setCustomFields((await PartnerCustomFieldService.getAll()).customFields)
      setUpdateFieldModal({
        editingParentField: updateFieldModal.editingParentField,
        show: false,
        loading: false,
        fieldId: '',
        form: { name: '', description: '' },
        errors: {}
      })
    } catch (error) {
      toast.error('Failed to update custom field.');
      setUpdateFieldModal(prev => ({ ...prev, loading: false }))
    }
  }

  // Delete handler
  const handleDeleteField = async (fieldId: string, fieldName: string) => {
    if (!confirm(`Are you sure you want to delete the custom field "${fieldName}"? This will also delete all sub-fields.`)) {
      return
    }

    try {
      await PartnerCustomFieldService.delete(fieldId)
      toast.success('Custom field has successfully been deleted.');
      setCustomFields((await PartnerCustomFieldService.getAll()).customFields)
    } catch (error: any) {
      if (error?.message?.includes('assigned to devices') ||
          error?.response?.status === 400) {
            toast.error(`Cannot delete custom field "${fieldName}" because it is currently assigned to one or more devices. Please remove the custom field from all devices before deleting it.`);
      } else {
        toast.error(`Failed to delete custom field "${fieldName}". Please try again.`);
      }
    }
  }

  // Modal components
  const renderCreateFieldModal = () => {
    if (!createFieldModal.show) return null

    return (

      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h2>{createFieldModal.parentId ? 'Add Sub-Field' : 'Add Parent Field'}</h2>
            <button className="modal-close" onClick={() => setCreateFieldModal(prev => ({ ...prev, show: false }))}>
              ×
            </button>
          </div>
          <form onSubmit={submitCreate}>
            <div className="modal-body">
              <div className="form-field-group">
                <label htmlFor="fieldName">Field Name *</label>
                <input
                  key={`create-name-${createFieldModal.show}-${createFieldModal.parentId}`}
                  id="fieldName"
                  className={`form-input ${createFieldModal.errors.name ? 'error' : ''}`}
                  placeholder="Enter field name"
                  value={createFieldModal.form.name}
                  onChange={e => handleCreateFormChange('name', e.target.value)}
                  maxLength={200}
                />
                {createFieldModal.errors.name && (
                  <div className="error-text">{createFieldModal.errors.name}</div>
                )}
              </div>

              <div className="form-field-group">
                <label htmlFor="fieldDescription">Description</label>
                <textarea
                  key={`create-desc-${createFieldModal.show}-${createFieldModal.parentId}`}
                  id="fieldDescription"
                  className="form-input"
                  placeholder="Enter field description (optional)"
                  value={createFieldModal.form.description}
                  onChange={e => handleCreateFormChange('description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setCreateFieldModal(prev => ({ ...prev, show: false }))}
                disabled={createFieldModal.loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createFieldModal.loading}
              >
                {createFieldModal.loading ? (
                  <>
                    <LoadingSpinner
                      height={16}
                      width={16}
                      color="#ffffff"
                      visible={true}
                      ariaLabel="loading-spinner"
                      secondaryColor="#cccccc"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  const renderUpdateFieldModal = () => {
    if (!updateFieldModal.show) return null

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
          {updateFieldModal.editingParentField ? (
              <h2>Update Field</h2>
            ) : (
              <h2>Update Sub-Field</h2>
            )
          }
            <button className="modal-close" onClick={() => setUpdateFieldModal(prev => ({ ...prev, show: false }))}>
              ×
            </button>
          </div>
          <form onSubmit={submitUpdate}>
            <div className="modal-body">
              <div className="form-field-group">
                <label htmlFor="updateFieldName">Field Name *</label>
                <input
                  id="updateFieldName"
                  className={`form-input ${updateFieldModal.errors.name ? 'error' : ''}`}
                  placeholder="Enter field name"
                  value={updateFieldModal.form.name}
                  onChange={e => handleUpdateFormChange('name', e.target.value)}
                  maxLength={200}
                />
                {updateFieldModal.errors.name && (
                  <div className="error-text">{updateFieldModal.errors.name}</div>
                )}
              </div>

              <div className="form-field-group">
                <label htmlFor="updateFieldDescription">Description</label>
                <textarea
                  id="updateFieldDescription"
                  className="form-input"
                  placeholder="Enter field description (optional)"
                  value={updateFieldModal.form.description}
                  onChange={e => handleUpdateFormChange('description', e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setUpdateFieldModal(prev => ({ ...prev, show: false }))}
                disabled={updateFieldModal.loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateFieldModal.loading}
              >
                {updateFieldModal.loading ? (
                  <>
                    <LoadingSpinner
                      height={16}
                      width={16}
                      color="#ffffff"
                      visible={true}
                      ariaLabel="loading-spinner"
                      secondaryColor="#cccccc"
                      strokeWidth={2}
                      strokeWidthSecondary={2}
                    />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <PageLayout
      title={PAGES.CUSTOM_FIELDS.title}
      heading={PAGES.CUSTOM_FIELDS.title}
      action={
        shouldGetCustomFields && customFields.length < CUSTOM_FIELD_CONFIG.MAX_PARENT_FIELD_COUNT && (
          <PermissionGuard
          resource={[RESOURCES.CUSTOM_FIELDS]}
          scope={PermissionScope.CREATE}
        >
          <button className="btn btn-primary" onClick={() => openCreateModal()}>
            Add Parent Field
          </button>
        </PermissionGuard>
        )
      }
    >

<div className="custom-fields-list">
              {/* Custom Field List or Empty State */}
              {customFields.length === 0 ? (
                <div className="empty-state">
                  <p>No custom fields found.</p>
                </div>
              ) : (
                organizedFields.map(parentField => (
                  <div key={parentField.id} className="custom-field-group">
                    {/* Parent Field Header */}
                    <div className="parent-field-header">
                      <div className="parent-field-info">
                        <button
                          className="expand-toggle"
                          onClick={() => toggleFieldExpansion(parentField.id)}
                        >
                          <span className={`expand-icon ${expandedFields.has(parentField.id) ? 'expanded' : ''}`}>
                            ▶
                          </span>
                        </button>

                        <div className="field-details">
                          <h3 className="field-name">
                            {parentField.name}
                            {parentField.isDefault && <span className="default-badge">Default</span>}

                          </h3>
                          {parentField.description && (
                            <p className="field-description">{parentField.description}</p>
                          )}
                        </div>
                      </div>

                        <div className="field-actions">
                        <PermissionGuard resource={RESOURCES.CUSTOM_FIELDS} scope={PermissionScope.CREATE}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openCreateModal(parentField.id)}
                          >
                            Add Sub-Field
                          </button>
                        </PermissionGuard>
                          
                        <PermissionGuard resource={RESOURCES.CUSTOM_FIELDS} scope={PermissionScope.UPDATE}>
                          <button
                            className="btn btn-secondary edit-btn"
                            onClick={() => openUpdateModal(parentField.id, parentField.name, true, parentField.description)}
                          >
                            Update
                          </button>
                        </PermissionGuard>

                        <PermissionGuard resource={RESOURCES.CUSTOM_FIELDS} scope={PermissionScope.DELETE}>
                          {!parentField.isDefault && (
                            <button
                              className="btn btn-secondary delete-btn"
                              onClick={() => handleDeleteField(parentField.id, parentField.name)}
                            >
                              Delete
                            </button>
                          )}
                        </PermissionGuard>
                        </div>
                    </div>

                    {/* Sub-Fields Section */}
                    {expandedFields.has(parentField.id) && (
                      <div className="sub-fields">
                        {parentField.subFields.length === 0 ? (
                          <div className="no-sub-fields">
                            <p>No sub-fields in this group yet.</p>
                          </div>
                        ) : (
                          <div className="sub-fields-list">
                            {parentField.subFields.map(subField => (
                              <div key={subField.id} className="sub-field-item">
                                <div className="sub-field-info">
                                  <h4 className="sub-field-name">{subField.name}</h4>
                                  {subField.description && (
                                    <p className="sub-field-description">{subField.description}</p>
                                  )}
                                </div>
                                  <div className="sub-field-actions">
                                  <PermissionGuard resource={RESOURCES.CUSTOM_FIELDS} scope={PermissionScope.UPDATE}>
                                    <button
                                      className="btn btn-secondary edit-btn"
                                      onClick={() => openUpdateModal(subField.id, subField.name, false, subField.description)}
                                    >
                                      Update
                                    </button>
                                  </PermissionGuard>
                                  <PermissionGuard resource={RESOURCES.CUSTOM_FIELDS} scope={PermissionScope.DELETE}>
                                    <button
                                      className="btn btn-secondary delete-btn"
                                      onClick={() => handleDeleteField(subField.id, subField.name)}
                                    >
                                      Delete
                                    </button>
                                  </PermissionGuard>
                                  </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
                )}
            </div>
      {renderCreateFieldModal()}
      {renderUpdateFieldModal()}
    </PageLayout>
  )
}
